import React, { useId, useState, FC, useEffect, useRef } from 'react';
import { StyledAmenititesModal } from './AmenitiesModal.styles';
import { AmenitiesModalProps, DiningPlanDetail } from './AmenitiesModal.types';
import { TimePicker } from '../../molecules/TimePicker';
import { Heading, Text, Types, Button, Calendar } from '@marriott/mi-ui-library';
import { useCreateSurveyStore } from '../../store/createSurveyStore';
import moment from 'moment';
import {
  partySizeValueDefaultOption,
  partySizeValue,
  SHORT_MONTH_NAME,
  SHORT_WEEK_DAYS,
  LONG_WEEK_DAYS_NAME,
  LONG_MONTH_NAME,
  CALENDAR_MAX_ALLOWED_DAYS,
} from '../../util/constants';
import dayjs from 'dayjs';
import DOMPurify from 'isomorphic-dompurify';

export const AmenitiesModal: FC<AmenitiesModalProps> = props => {
  const {
    onClose,
    componentType,
    subtitle,
    index,
    handleButton,
    arrivalDate,
    departureDate,
    desc,
    title,
    subcategory,
  } = props;

  const onlyArrivalDate = moment(arrivalDate).format('YYYY-MM-DD');
  const onlyDepartureDate = dayjs(departureDate).format('YYYY-MM-DD');

  const mainHeading = `Details for your ${componentType} Request`;
  const subTitle1 = desc;
  const subTitle2 = 'Party Size<span class="red-asterisk">*</span>';
  const subTitle3 = 'Preferred Date Request<span class="red-asterisk">*</span>';
  const subTitle4 = 'Alternative Date Request (optional)';
  const subTitle5 = 'Please work directly with the hotel to arrange large party reservations of 10 or more people.';
  const timeTitle = 'Hotel local time';
  const partysizeErrorMsg = 'Please enter number of guests in your party ';
  const preferredDateErrorMsg = 'Please enter preferred date ';
  const preferredTimeErrorMsg = 'Please enter preferred time ';

  const [selectedTimes, setSelectedTimes] = useState({
    preferredTime: '',
    alternateTime: '',
  });

  const [partySize, setPartySize] = useState(partySizeValueDefaultOption.value);
  const [preferredDate, setPreferredDate] = useState('');
  // const [displaypreferredDate, setDisplayPreferredDate] = useState('');
  const [alternativeDate, setAlternativeDate] = useState('');
  // const [displayalternativeDate, setDisplayAlternativeDate] = useState('');
  const [showPreferredCalendar, setPreferredShowCalendar] = useState(false);
  const [showAlternateCalendar, setShowAlternateCalendar] = useState(false);

  const [errors, setErrors] = useState({ partysize: '', preferredDate: '', preferredTime: '' });

  const preferredCalendarRef = useRef<HTMLDivElement>(null); // Ref for preferred calendar container
  const alternateCalendarRef = useRef<HTMLDivElement>(null); // Ref for alternate calendar container

  const { formData, setFormData } = useCreateSurveyStore();
  const amenitiesDataList = formData?.prearrivalformamenitiesaddedtoplan;
  const inputId = useId();

  useEffect(() => {
    // Add styles directly to body when modal opens
    document.body.style.overflow = 'hidden';
    // Function to handle clicks outside the calendar container
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      const isClickInsidePreferred = preferredCalendarRef.current?.contains(target);
      const isClickInsideAlternate = alternateCalendarRef.current?.contains(target);

      // If the click is outside both calendars, close them
      if (!isClickInsidePreferred && !isClickInsideAlternate) {
        setPreferredShowCalendar(false);
        setShowAlternateCalendar(false);
      }
    };
    // Add event listener for clicks outside
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup function to remove styles when component unmounts or modal closes
    return () => {
      document.body.style.overflow = 'auto';
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleTimeChange = (newTime: string, type: 'preferredTime' | 'alternateTime') => {
    setSelectedTimes({ ...selectedTimes, [type]: newTime });
    setErrors({ ...errors, preferredTime: '' });
  };
  const disabledDays = [
    // Dates before arrival date
    ...getDatesInRange(new Date(0), moment(onlyArrivalDate)),
    // Dates after departure date
    ...getDatesInRange(onlyDepartureDate, new Date(9999999999999)),
  ];

  // Helper function to generate dates in a range
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function getDatesInRange(start: any, end: any): Date[] {
    const dates = [];
    const currentDate = new Date(start);
    if (currentDate < end) {
      currentDate.setDate(currentDate.getDate() + 1);
    }
    while (currentDate < end) {
      dates.push(new Date(currentDate)); // Add a copy of the current date
      currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
    }

    return dates;
  }

  const handleSubmit = () => {
    const newErrors = { partysize: '', preferredDate: '', preferredTime: '' };
    if (!partySize) newErrors.partysize = partysizeErrorMsg;
    if (!preferredDate) newErrors.preferredDate = preferredDateErrorMsg;
    if (!selectedTimes.preferredTime) newErrors.preferredTime = preferredTimeErrorMsg;
    if (newErrors.partysize || newErrors.preferredDate || newErrors.preferredTime) {
      setErrors(newErrors);
    } else {
      const newDetail: DiningPlanDetail = {
        dining_location: subtitle,
        party_size: partySize,
        prefered_date: `${preferredDate}`,
        prefered_time: `${selectedTimes.preferredTime}`,
        alternate_date: `${alternativeDate}`,
        alternate_time: `${selectedTimes.alternateTime}`,
        selected_index: index,
        reservation_req: '1',
      };
      const detailKey = componentType !== 'Experience' ? 'dining_plan_details' : 'experiences_plan_details';

      const updatedFormDataList = [...amenitiesDataList, subcategory != '' ? subcategory : title];

      setFormData({
        ...formData,
        [detailKey]: [...(formData[detailKey] || []), newDetail],
        prearrivalformamenitiesaddedtoplan: updatedFormDataList,
      });

      // Close the modal after form submission
      onClose();
    }
  };

  const onClosebtn = () => {
    handleButton();
  };
  const handleInputClick = (type: string) => {
    if (type === 'preferred') {
      setPreferredShowCalendar(true);
      setShowAlternateCalendar(false);
    } else {
      setShowAlternateCalendar(true);
      setPreferredShowCalendar(false);
    }
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDateChange = (date: any) => {
    setPreferredDate(moment(date).format('YYYY-MM-DD'));
    setErrors({ ...errors, preferredDate: '' });
    setPreferredShowCalendar(false); // Hide calendar after date selection
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleAlternateDateChange = (date: any) => {
    setAlternativeDate(moment(date).format('YYYY-MM-DD'));
    setShowAlternateCalendar(false); // Hide calendar after date selection
  };
  const sanitizedNode1 = DOMPurify.sanitize(subTitle1, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString1 = sanitizedNode1.innerHTML;

  const sanitizedNode2 = DOMPurify.sanitize(subTitle2, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString2 = sanitizedNode2.innerHTML;

  const sanitizedNode3 = DOMPurify.sanitize(subTitle3, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString3 = sanitizedNode3.innerHTML;

  const sanitizedNode4 = DOMPurify.sanitize(subTitle4, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString4 = sanitizedNode4.innerHTML;

  const sanitizedNode5 = DOMPurify.sanitize(subTitle5, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString5 = sanitizedNode5.innerHTML;

  const sanitizedNode6 = DOMPurify.sanitize(timeTitle, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString6 = sanitizedNode6.innerHTML;

  return (
    <StyledAmenititesModal data-testid="amenitites-modal" data-component-name="o-account-amenititesModal">
      <div className="prearrival-modal-overlay">
        <div className="dining-request-modal">
          <div>
            <div className="row">
              <div className="col-12 pr-0 px-0 modal-wrapper">
                <div className="close-button-container">
                  <button className="close-button" onClick={onClosebtn}>
                    &#10006;
                  </button>
                </div>
                <Heading
                  variation={Types.headingType.title}
                  fontSize={Types.size.medium}
                  element={Types.tags.h1}
                  titleText={mainHeading}
                  disableCustomClickTrack={true}
                  customClass="mb-4 modal-heading"
                />
                <div className="inside-div">
                  <Text
                    element={Types.tags.paragraph}
                    fontSize={Types.size.medium}
                    dangerouslySetInnerHTML={{ __html: fragmentString1 }}
                    customClass="mb-4 subtitle"
                  ></Text>
                  <div className="inside-div2">
                    <Text
                      element={Types.tags.paragraph}
                      fontSize={Types.size.medium}
                      dangerouslySetInnerHTML={{ __html: fragmentString2 }}
                      customClass="mb-3"
                    />
                    <div className="custom-select-wrapper party-size">
                      <select
                        className={`modal-select ${errors.partysize ? 'error-select-border' : ''}`}
                        id={`${inputId}-partySizeValue`}
                        name="partySizeValue"
                        value={partySize}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          setPartySize(e.target.value);
                          setErrors({ ...errors, partysize: '' });
                        }}
                        required
                      >
                        <option value={partySizeValueDefaultOption.value}>{partySizeValueDefaultOption.label}</option>
                        {partySizeValue.map((option, index) => (
                          <option key={index} value={option.value} role="option">
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <Text
                      element={Types.tags.paragraph}
                      fontSize={Types.size.extraSmall}
                      dangerouslySetInnerHTML={{ __html: fragmentString5 }}
                      customClass="mt-1  party-size-text"
                    ></Text>
                    {errors.partysize && (
                      <div className="error-container mt-1">
                        <span className="error-icon">
                          <i className="icon-alert"></i>
                        </span>
                        <Text
                          element={Types.tags.paragraph}
                          fontSize={Types.size.extraSmall}
                          customClass={`error-message time`}
                          dangerouslySetInnerHTML={{ __html: errors.partysize }}
                        ></Text>
                      </div>
                    )}

                    <Text
                      element={Types.tags.paragraph}
                      fontSize={Types.size.medium}
                      dangerouslySetInnerHTML={{ __html: fragmentString3 }}
                      customClass="mb-3 mt-4"
                    ></Text>

                    <div className="d-flex container-width">
                      <div
                        className={`date-picker-container ${preferredDate ? 'addVal' : ''}`}
                        ref={preferredCalendarRef}
                      >
                        <input
                          type="text"
                          className={`date-picker ${errors.preferredDate ? 'error-select-border' : ''}`}
                          value={preferredDate}
                          aria-label="Date picker"
                          onClick={() => handleInputClick('preferred')}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setPreferredDate(e.target.value);
                            setErrors({ ...errors, preferredDate: '' });
                          }}
                          placeholder="yyyy-mm-dd"
                        />
                        <span className="input-icon">
                          {/* You can use an icon from a library like FontAwesome or an image here */}
                          <i className="icon-stay-dates"></i>
                        </span>
                        {showPreferredCalendar && (
                          <Calendar
                            singleDatePicker={true}
                            className="singledatepicker"
                            startFromToday={false}
                            variation=""
                            startDate={preferredDate ? moment(preferredDate) : moment(onlyArrivalDate)}
                            endDate={null}
                            focusedInput={null}
                            setDateHandler={date => {
                              handleDateChange(date); // Update preferredDate and hide calendar
                            }}
                            setFocusedInputHandler={(): void => {
                              console.log('setFocusedInputHandler');
                            }}
                            nightsLabel=""
                            nightLabel=""
                            disableHoverToolTip={true}
                            maxAllowedDays={CALENDAR_MAX_ALLOWED_DAYS}
                            weekdays={SHORT_WEEK_DAYS}
                            months={LONG_MONTH_NAME}
                            weekdaysLong={LONG_WEEK_DAYS_NAME}
                            weekdaysShort={SHORT_WEEK_DAYS}
                            monthsShort={SHORT_MONTH_NAME}
                            firstDayOfWeek="0"
                            numberOfMonths={1}
                            showOutsideDays={true}
                            fixedWeeks={true}
                            disabledDays={disabledDays}
                            enableOutsideDaysClick={false}
                          />
                        )}

                        {errors.preferredDate && (
                          <div className="error-container mt-1">
                            <span className="error-icon">
                              <i className="icon-alert"></i>
                            </span>
                            <Text
                              element={Types.tags.paragraph}
                              fontSize={Types.size.extraSmall}
                              customClass={`error-message`}
                              dangerouslySetInnerHTML={{ __html: errors.preferredDate }}
                            ></Text>
                          </div>
                        )}
                      </div>

                      <div className={`time-picker d-flex flex-column  ${errors.preferredTime ? 'error-border' : ''}`}>
                        <TimePicker
                          id={`${inputId}-PreferredDate `}
                          name="PreferredDate"
                          time={selectedTimes.preferredTime}
                          onChange={newTime => handleTimeChange(newTime, 'preferredTime')}
                        />

                        <Text
                          element={Types.tags.paragraph}
                          fontSize={Types.size.small}
                          dangerouslySetInnerHTML={{ __html: fragmentString6 }}
                          customClass="time-text mt-2"
                        ></Text>
                        {errors.preferredTime && (
                          <div className="error-container mt-1 ml-2">
                            <span className="error-icon">
                              <i className="icon-alert "></i>
                            </span>
                            <Text
                              element={Types.tags.paragraph}
                              fontSize={Types.size.extraSmall}
                              customClass={`error-message`}
                              dangerouslySetInnerHTML={{ __html: errors.preferredTime }}
                            ></Text>
                          </div>
                        )}
                      </div>
                    </div>
                    <Text
                      element={Types.tags.paragraph}
                      fontSize={Types.size.medium}
                      dangerouslySetInnerHTML={{ __html: fragmentString4 }}
                      customClass="mb-3 mt-4"
                    ></Text>
                    <div className="d-flex container-width">
                      <div
                        className={`date-picker-container ${alternativeDate ? 'addVal' : ''}`}
                        ref={alternateCalendarRef}
                      >
                        <input
                          type="text"
                          className="date-picker"
                          value={alternativeDate}
                          aria-label="date-picker"
                          onClick={() => handleInputClick('alternative')}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setAlternativeDate(e.target.value);
                          }}
                          placeholder="yyyy-mm-dd"
                        />
                        <span className="input-icon">
                          {/* You can use an icon from a library like FontAwesome or an image here */}
                          <i className="icon-stay-dates"></i>
                        </span>
                        {showAlternateCalendar && (
                          <Calendar
                            singleDatePicker={true}
                            className="singledatepicker"
                            startFromToday={false}
                            variation="DESKTOP"
                            startDate={alternativeDate ? moment(alternativeDate) : moment(onlyArrivalDate)}
                            endDate={null}
                            focusedInput={null}
                            setDateHandler={date => {
                              handleAlternateDateChange(date); // Update preferredDate and hide calendar
                            }}
                            setFocusedInputHandler={(): void => {
                              console.log('setFocusedInputHandler');
                            }}
                            nightsLabel=""
                            nightLabel=""
                            disableHoverToolTip={true}
                            maxAllowedDays={CALENDAR_MAX_ALLOWED_DAYS}
                            weekdays={SHORT_WEEK_DAYS}
                            months={LONG_MONTH_NAME}
                            weekdaysLong={LONG_WEEK_DAYS_NAME}
                            weekdaysShort={SHORT_WEEK_DAYS}
                            monthsShort={SHORT_MONTH_NAME}
                            firstDayOfWeek="0"
                            numberOfMonths={1}
                            showOutsideDays={true}
                            fixedWeeks={true}
                            disabledDays={disabledDays}
                          />
                        )}
                      </div>
                      <div className="time-picker d-flex flex-column">
                        <TimePicker
                          id={`${inputId}-AlternateDate`}
                          name="AlternateDate"
                          time={selectedTimes.alternateTime}
                          onChange={newTime => handleTimeChange(newTime, 'alternateTime')}
                        />
                        <Text
                          element={Types.tags.paragraph}
                          fontSize={Types.size.small}
                          dangerouslySetInnerHTML={{ __html: fragmentString6 }}
                          customClass="time-text mt-2"
                        ></Text>
                      </div>
                    </div>
                    <div className="col-12 p-0 mt-4">
                      <Button
                        className="m-button-m m-button-primary custom-btn mx-0"
                        type={Types.ButtonTypeVariation.Button}
                        buttonCopy="Submit Request"
                        callback={handleSubmit}
                        id="submitRequest"
                      ></Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StyledAmenititesModal>
  );
};
